import { apv2MutualExclusiveFields, apv2ToTreeCongViecTiTrong } from '@/components/common/ActionPlanV2/core/tiTrong';
import { toActionPlanCalendarDate } from '@/components/common/ActionPlanV2/core/date';
import { actionPlanV2Api } from '@/services/action-plan-v2';
import { ACTION_PLAN_V2 } from '@/components/common/ActionPlanV2/constants';
import type { CongViecDuAnCheck, DuAnCongViecTree } from '@/types/action-plan/DuAnView';
import type { DuAnCongViecDTO } from '@/types/action-plan/ActionPlan/DuAnCongViecDTO';
import type { TableRecord } from '@/components/common/ActionPlan/CongViecDuAn/Right/Table/types';
import type { EmployeeHomeMucTieuGroup, EmployeeHomeSummaryResponse } from '@/types/action-plan-v2/EmployeeHome';
import { normalizeEmployeeHomeSummaryResponse } from '@/components/common/ActionPlanV2/core/employeeHome';
import {
  mapLoadMucTieuV2Response,
  type MucTieuFilterData,
  type MucTieuListItem,
  APV2_ROOT_TASK_PARENT_ID,
  isApv2RootTaskParentId,
  isDisplayMucTieuRecord,
  MUC_TIEU_DISPLAY_ID_PREFIX,
} from './mucTieu';

function isApv2VisibleMucTieuRootTask(
  cv:
    | { isDuAn?: boolean; level?: number; parentID?: string | null; id?: string; duAnID?: string }
    | undefined
): boolean {
  if (!cv) return false;

  const parentId = String(cv.parentID ?? '').trim();

  // Root AP V2: isDuAn = true, parent rỗng hoặc dưới node mục tiêu
  if (cv.isDuAn === true) {
    if (isApv2RootTaskParentId(parentId)) return true;
    if (parentId.startsWith(MUC_TIEU_DISPLAY_ID_PREFIX)) return true;
    return false;
  }

  // Dữ liệu cũ (migration): isDuAn = false, DuAnID = ID
  if (isApv2RootTaskParentId(parentId)) return true;
  if (parentId.startsWith(MUC_TIEU_DISPLAY_ID_PREFIX)) return true;

  const id = String(cv.id ?? '').trim();
  const duAnId = String(cv.duAnID ?? '').trim();
  if (id && duAnId && id === duAnId) return true;

  return false;
}

/** Wrapper dự án ẩn legacy — không áp dụng task gốc AP V2 (parentID rỗng dưới mục tiêu). */
function isApv2LegacyHiddenDuAnWrapper(
  cv: { level?: number; id?: string; duAnID?: string; parentID?: string | null; isDuAn?: boolean },
  childParentId: string
): boolean {
  if (isApv2VisibleMucTieuRootTask(cv)) return false;
  const id = String(cv.id ?? '').trim();
  const duAnId = String(cv.duAnID ?? '').trim();
  if (!childParentId || childParentId !== id) return false;
  if (cv.isDuAn === true) return true;
  if ((cv.level ?? 0) === 0 && duAnId && id === duAnId) return true;
  return false;
}

export type LoadMucTieuWorkspaceParams = {
  mtctid: string;
  idSelect: string;
  year: number;
  dateChoice?: string;
  kieuTrinhBay?: string;
  /** '1' phụ trách | '2' đã giao | '3' hỗ trợ */
  activeTab?: string;
};

export type LoadMucTieuListParams = Omit<LoadMucTieuWorkspaceParams, 'mtctid'> & {
  /** pt | ht — gửi kèm includeEmployeeHomeSummary trên EmployeeHome. */
  filter?: 'pt' | 'ht';
  includeEmployeeHomeSummary?: boolean;
};

export type MucTieuListLoadResult = {
  items: MucTieuListItem[];
  filterData: MucTieuFilterData | null;
  summary?: EmployeeHomeSummaryResponse | null;
};

/** Checklist (tỉ trọng / viTri khi lưu) từ các node đã tải — cây lazy: nhóm anh em đã tải luôn đủ (expand trả mọi con). */
export function buildCheckListFromForestTasks(tasks: DuAnCongViecTree[]): CongViecDuAnCheck[] {
  const list: CongViecDuAnCheck[] = [];
  const walk = (nodes: DuAnCongViecTree[]) => {
    for (const node of nodes ?? []) {
      const cv = node.congViec;
      if (cv?.id) {
        const parentRaw = String(cv.parentID ?? '').trim();
        list.push({
          id: String(cv.id),
          parentID: isApv2RootTaskParentId(parentRaw) ? APV2_ROOT_TASK_PARENT_ID : parentRaw,
          duanID: String(cv.duAnID ?? '').trim() || undefined,
          nganSach: Number(cv.nganSach ?? 0),
          tiTrong: cv.tiTrong ?? null,
          tiTrongCal: cv.tiTrongCal ?? null,
          tiTrongV2: cv.tiTrongV2 ?? null,
          tiTrongCalV2: cv.tiTrongCalV2 ?? null,
          ngayBatDau: toActionPlanCalendarDate(cv.ngayBatDau),
          ngayKetThuc: toActionPlanCalendarDate(cv.ngayKetThuc),
          viTri: cv.viTri ?? null,
          hasChange: false,
        });
      }
      if (node.childs?.length) walk(node.childs);
    }
  };
  walk(tasks ?? []);
  return list;
}

/** Dự án gốc ẩn (isDuAn / level 0 id=duAnID) có con — bỏ wrapper, giữ công việc hiển thị. */
function isApv2HiddenProjectRootWrapper(node: DuAnCongViecTree): boolean {
  const cv = node.congViec;
  if (!cv) return false;
  const childs = node.childs ?? [];
  if (childs.length === 0) return false;
  if (isApv2VisibleMucTieuRootTask(cv)) return false;
  if (cv.isDuAn === true) return true;
  const id = String(cv.id ?? '').trim();
  const duAnId = String(cv.duAnID ?? '').trim();
  if (
    (cv.level ?? 0) === 0 &&
    id &&
    duAnId &&
    id === duAnId &&
    !isApv2RootTaskParentId(cv.parentID)
  ) {
    return true;
  }
  const firstChild = childs[0];
  if (firstChild && isApv2LegacyHiddenDuAnWrapper(cv, String(firstChild.congViec?.parentID ?? ''))) {
    return true;
  }
  return false;
}

function unwrapApv2MucTieuTaskRoots(tasks: DuAnCongViecTree[]): DuAnCongViecTree[] {
  const out: DuAnCongViecTree[] = [];
  for (const task of tasks ?? []) {
    if (isApv2HiddenProjectRootWrapper(task)) {
      out.push(...unwrapApv2MucTieuTaskRoots(task.childs ?? []));
      continue;
    }
    const childs = unwrapApv2MucTieuTaskRoots(task.childs ?? []);
    out.push({
      ...task,
      childs,
      hasChild: childs.length > 0,
      countChilds: childs.length,
    });
  }
  return out;
}

type Apv2ViTriSortable = {
  isPlaceholder?: boolean;
  hasChild?: boolean;
  countChilds?: number;
  congViec?: {
    id?: string;
    parentID?: string;
    viTri?: number | null;
    isDisplayMucTieu?: boolean;
  };
  childs?: Apv2ViTriSortable[];
};

function compareApv2SiblingViTri<T extends Apv2ViTriSortable>(a: T, b: T): number {
  const av = Number(a.congViec?.viTri ?? 0);
  const bv = Number(b.congViec?.viTri ?? 0);
  if (av !== bv) return av - bv;
  return String(a.congViec?.id ?? '').localeCompare(String(b.congViec?.id ?? ''));
}

/**
 * Sắp sibling theo viTri (không mutate khi thứ tự đã đúng).
 * - Node display MT: giữ thứ tự mục tiêu.
 * - pt root (parentID rỗng): luôn sort viTri — kể cả khi cả nhóm đều là pt (BE xếp root theo id).
 * - Mixed pt + DG: pt sort viTri trước, DG giữ thứ tự gốc.
 * - Con trong 1 task: sort cả nhóm theo viTri.
 */
export function orderApv2SiblingRecordsByViTri<T extends Apv2ViTriSortable>(items: T[]): T[] {
  if (!items?.length || items.length <= 1) return items;
  if (items.some((item) => isDisplayMucTieuRecord(item))) return items;

  const real: T[] = [];
  const placeholders: T[] = [];
  for (const item of items) {
    if (item.isPlaceholder) placeholders.push(item);
    else real.push(item);
  }
  if (real.length <= 1) return items;

  const ptRoots: T[] = [];
  const others: T[] = [];
  for (const node of real) {
    if (isApv2RootTaskParentId(node.congViec?.parentID)) ptRoots.push(node);
    else others.push(node);
  }

  const sortedPt = ptRoots.length > 1 ? [...ptRoots].sort(compareApv2SiblingViTri) : ptRoots;
  const sortedOthers =
    !ptRoots.length && others.length > 1 ? [...others].sort(compareApv2SiblingViTri) : others;
  const ordered = ptRoots.length
    ? others.length
      ? [...sortedPt, ...sortedOthers]
      : sortedPt
    : sortedOthers;
  const next = placeholders.length ? [...ordered, ...placeholders] : ordered;
  if (next.length === items.length && next.every((node, idx) => node === items[idx])) return items;
  return next;
}

export function sortApv2ForestChildsByViTriDeep<T extends Apv2ViTriSortable>(nodes: T[]): T[] {
  if (!nodes?.length) return nodes;
  const ordered = orderApv2SiblingRecordsByViTri(nodes);
  let nestedChanged = false;
  const next = ordered.map((node) => {
    const rawChilds = node.childs as T[] | undefined;
    if (!rawChilds?.length) return node;
    const childs = sortApv2ForestChildsByViTriDeep(rawChilds);
    if (childs === rawChilds) return node;
    nestedChanged = true;
    return {
      ...node,
      childs,
      hasChild: childs.length > 0,
      countChilds: childs.length,
    } as T;
  });
  if (!nestedChanged && next.length === nodes.length && next.every((node, idx) => node === nodes[idx])) {
    return nodes;
  }
  return next;
}

/**
 * Sắp xếp childs node display MT: pt root (parentID rỗng) theo viTri trước (ổn định vị trí
 * task mới = cuối nhóm pt sau reload, vì BE BuildTreeV2 xếp root theo id chữ cái), DG giữ
 * nguyên thứ tự gốc. Con trong từng task cũng sort theo viTri.
 */
function sortMucTieuDisplayChildsByGroup(childs: DuAnCongViecTree[]): DuAnCongViecTree[] {
  return sortApv2ForestChildsByViTriDeep(childs);
}

/** Sắp cây bảng theo viTri (load/sync) — không clone khi thứ tự đã đúng. */
export function sortApv2ForestTableRecordsByViTri<T extends Apv2ViTriSortable>(items: T[]): T[] {
  return sortApv2ForestChildsByViTriDeep(items);
}

export function buildMucTieuDisplayNode(
  mtctid: string,
  mucTieuName: string,
  tasks: DuAnCongViecTree[],
  columnConfigs?: EmployeeHomeMucTieuGroup['columnConfigs']
): DuAnCongViecTree {
  const childs = sortMucTieuDisplayChildsByGroup(normalizeDuAnCongViecTrees(unwrapApv2MucTieuTaskRoots(tasks)));
  const displayId = `${MUC_TIEU_DISPLAY_ID_PREFIX}${mtctid}`;
  return {
    congViec: {
      id: displayId,
      tenCongViec: mucTieuName || mtctid,
      mtctid,
      level: 0,
      parentID: '',
      isDisplayMucTieu: true,
      treePath: displayId,
      treePathName: mucTieuName || mtctid,
    } as unknown as DuAnCongViecDTO,
    thoiGian: '',
    hasInRange: true,
    hasChange: false,
    hasChild: childs.length > 0,
    countChilds: childs.length,
    childs,
    param: '',
    trongKy: { icon: '', titleIcon: '', giaiDoan: '', tienDoHomNay: 0, hoanThanh: 0, trangThai: 1 },
    luyke: { icon: '', titleIcon: '', giaiDoan: '', tienDoHomNay: 0, hoanThanh: 0, trangThai: 1 },
    tenTanSuat: '',
    tenHinhThucTinh: '',
    roleItemTree: {
      isNguoiTao: false,
      isPhuTrach: false,
      isHoTro: false,
      hasChild: childs.length > 0,
      isEdit: false,
      hasAddSub: false,
      isRoot: true,
      isView: true,
      isDelete: false,
    },
    tiTrongDaAn: 0,
    tiTrongTatCaDaAn: 0,
    columnConfigs: columnConfigs ?? [],
    listChiTieu: [],
  } as DuAnCongViecTree;
}

export function flattenCongViecTableRecordsForSave(records: TableRecord[]): TableRecord[] {
  const walk = (items: TableRecord[]): TableRecord[] =>
    items.flatMap((item): TableRecord[] => {
      if (item.isPlaceholder) return [];
      // Node mục tiêu chỉ trên UI — không gửi API; con gốc thật → ParentID rỗng.
      // Việc được giao (DG) hiện ngay dưới MT nhưng có cha thật trong DB → giữ ParentID (không thành gốc).
      if (isDisplayMucTieuRecord(item)) {
        const childs = ((item.childs || []) as TableRecord[]).map((child) => {
          const ownParentId = String(child.congViec?.parentID ?? '').trim();
          const cv = child.congViec
            ? {
                ...child.congViec,
                parentID: isApv2RootTaskParentId(ownParentId) ? APV2_ROOT_TASK_PARENT_ID : ownParentId,
              }
            : child.congViec;
          return { ...child, congViec: cv, childs: child.childs ?? [] } as TableRecord;
        });
        return walk(childs);
      }
      const childs = item.childs?.length
        ? walk(item.childs as TableRecord[])
        : ((item.childs ?? []) as TableRecord[]);
      const parentID = String(item.congViec?.parentID ?? '').trim();
      const cv = item.congViec
        ? { ...item.congViec, parentID }
        : item.congViec;
      return [{ ...item, congViec: cv, childs } as TableRecord];
    });
  return walk(records);
}

/** Tổng tỉ trọng tối đa cho nhóm công việc gốc dưới một mục tiêu. */
export const APV2_MUC_TIEU_TI_TRONG_MAX = 100;

export function isMucTieuForestRootTableData(items: TableRecord[]): boolean {
  return (items || []).some((item) => !item.isPlaceholder && isDisplayMucTieuRecord(item));
}
export function resolveMtctidForNewRootTask(
  items: TableRecord[],
  listMucTieu?: { muctieuID?: string; muctieuName?: string }[]
): string {
  // Ưu tiên mục tiêu ĐANG HIỂN THỊ (user đang nhìn) — tránh thêm vào mục tiêu đầu đã chọn
  // nhưng không nằm trong cây hiện tại (group theo cột động → nhiều sheet).
  const firstDisplay = (items || []).find((item) => !item.isPlaceholder && isDisplayMucTieuRecord(item));
  if (firstDisplay) return String(firstDisplay?.congViec?.mtctid ?? '');
  const fromList = listMucTieu?.find((item) => item?.muctieuID)?.muctieuID;
  if (fromList) return String(fromList);
  return '';
}

/** Cả id đầy đủ và phần sau `_` — khớp listMucTieuPhuTrachIDs. */
export function normalizeMtctidKeys(mtctid: string): string[] {
  const trimmed = String(mtctid ?? '').trim();
  if (!trimmed) return [];
  const clean = trimmed.includes('_') ? trimmed.split('_').pop() || trimmed : trimmed;
  return clean === trimmed ? [trimmed] : [trimmed, clean];
}

/** undefined = không lọc (Tổng quan); có set = chỉ MT phụ trách. */
export function buildPhuTrachMtctidSet(ids?: readonly string[] | null): Set<string> | undefined {
  if (!ids?.length) return undefined;
  const set = new Set<string>();
  for (const id of ids) {
    for (const key of normalizeMtctidKeys(id)) set.add(key);
  }
  return set;
}

export function isMtctidInPhuTrachSet(mtctid: string, set: Set<string> | undefined): boolean {
  if (!set) return true;
  const trimmed = String(mtctid ?? '').trim();
  if (!trimmed) return false;
  for (const key of normalizeMtctidKeys(trimmed)) {
    if (set.has(key)) return true;
  }
  return false;
}

/** Prefix hiển thị trước tên MT — màn theo mục tiêu; Tổng quan = `Mục tiêu:`. */
export function resolveMucTieuLabelPrefix(
  mtctid: string,
  phuTrachIds?: readonly string[] | null
): string {
  const set = buildPhuTrachMtctidSet(phuTrachIds);
  if (!set || !String(mtctid ?? '').trim()) return 'Mục tiêu:';
  return isMtctidInPhuTrachSet(mtctid, set)
    ? 'Mục tiêu phụ trách:'
    : 'Mục tiêu không phụ trách:';
}

/**
 * Resolve `mtctid` cho 1 dòng đang thao tác (vd context menu):
 * 1. `congViec.mtctid` trực tiếp (nếu có)
 * 2. leo lên cha theo `parentID` / display-node tổ tiên (task load từ BE có thể thiếu mtctid)
 * 3. fallback: mục tiêu đầu đang hiển thị
 */
export function resolveRecordMtctid(
  record?: TableRecord | null,
  items?: TableRecord[]
): string {
  if (!record?.congViec) return '';
  const direct = String(record.congViec.mtctid ?? '').trim();
  if (direct) return direct;

  const list = items || [];
  if (record.isPlaceholder && list.length) {
    const findMtctidForPlaceholder = (nodes: TableRecord[], ancestorDisplayMtctid = ''): string => {
      for (const node of nodes) {
        const isDisplay = isDisplayMucTieuRecord(node);
        const displayMtctid = isDisplay
          ? String(node.congViec?.mtctid ?? '').trim() || ancestorDisplayMtctid
          : ancestorDisplayMtctid;
        if (node.isPlaceholder && node.key === record.key) {
          return displayMtctid;
        }
        if (node.childs?.length) {
          const found = findMtctidForPlaceholder(node.childs as TableRecord[], displayMtctid);
          if (found) return found;
        }
      }
      return '';
    };
    const fromTree = findMtctidForPlaceholder(list);
    if (fromTree) return fromTree;
  }

  const id = String(record.congViec.id ?? record.key ?? '');
  if (id) {
    const walk = (nodes: TableRecord[], displayMtctid?: string): string => {
      for (const node of nodes) {
        if (node.isPlaceholder) continue;
        const isDisplay = isDisplayMucTieuRecord(node);
        const nextDisplayMtctid = isDisplay
          ? String(node.congViec?.mtctid ?? '').trim() || displayMtctid
          : displayMtctid;
        const nodeId = String(node.congViec?.id ?? node.key ?? '');
        if (nodeId === id) {
          const own = String(node.congViec?.mtctid ?? '').trim();
          return own || nextDisplayMtctid || '';
        }
        if (node.childs?.length) {
          const found = walk(node.childs as TableRecord[], nextDisplayMtctid);
          if (found) return found;
        }
      }
      return '';
    };
    const found = walk(list);
    if (found) return found;
  }
  return resolveMtctidForNewRootTask(list, []);
}

export function resolveApv2DuAnIdForRecord(record: TableRecord): string {
  const cv = record.congViec;
  if (!cv) return '';
  const duAnId = String(cv.duAnID ?? '').trim();
  if (duAnId && duAnId !== 'undefined') return duAnId;
  const id = String(cv.id ?? record.key ?? '').trim();
  if (isApv2RootTaskParentId(cv.parentID)) return id;
  const treeRoot = cv.treePath?.split('/')[0];
  if (treeRoot && treeRoot !== 'undefined') return treeRoot;
  return id;
}

/** Mỗi cây root mới: parentID rỗng, duAnID = chính id công việc. */
export function applyApv2RootTaskFields(task: TableRecord, mtctid: string): TableRecord {
  const taskId = String(task.congViec?.id ?? task.key ?? '');
  const now = toActionPlanCalendarDate(new Date()) ?? new Date();
  const cv = task.congViec;
  return {
    ...task,
    congViec: {
      ...cv,
      isDuAn: true,
      parentID: APV2_ROOT_TASK_PARENT_ID,
      level: 1,
      mtctid: mtctid || cv?.mtctid,
      duAnID: taskId,
      treePath: taskId,
      tiTrong: undefined,
      tiTrongCal: APV2_MUC_TIEU_TI_TRONG_MAX,
      tiTrongV2: undefined,
      tiTrongCalV2: APV2_MUC_TIEU_TI_TRONG_MAX,
      ngayBatDau: toActionPlanCalendarDate(cv?.ngayBatDau) ?? now,
      ngayKetThuc: toActionPlanCalendarDate(cv?.ngayKetThuc) ?? now,
    },
  };
}

/** Con: ParentID/DuAnID/TreePath/MTCTID theo cha thật — không gắn apv2-mt-*. */
export function applyApv2ChildTaskFields(child: TableRecord, parent: TableRecord): TableRecord {
  const parentId = String(parent.congViec?.id ?? parent.key ?? '').trim();
  const parentTreePath = String(parent.congViec?.treePath ?? parentId).trim();
  const childId = String(child.congViec?.id ?? child.key ?? '');
  const now = toActionPlanCalendarDate(new Date()) ?? new Date();
  const cv = child.congViec;
  const parentCv = parent.congViec;
  const parentStart = toActionPlanCalendarDate(parentCv?.ngayBatDau);
  const parentEnd = toActionPlanCalendarDate(parentCv?.ngayKetThuc);
  return {
    ...child,
    congViec: {
      ...cv,
      parentID: parentId,
      isDuAn: false,
      duAnID: resolveApv2DuAnIdForRecord(parent),
      level: (parentCv?.level ?? 0) + 1,
      mtctid: cv?.mtctid ?? parentCv?.mtctid,
      boPhanPhuTrachID: cv?.boPhanPhuTrachID || parentCv?.boPhanPhuTrachID,
      treePath: parentTreePath && childId ? `${parentTreePath}/${childId}` : childId,
      tiTrong: undefined,
      tiTrongCal: undefined,
      tiTrongV2: undefined,
      tiTrongCalV2: undefined,
      ngayBatDau: toActionPlanCalendarDate(cv?.ngayBatDau) ?? parentStart ?? now,
      ngayKetThuc: toActionPlanCalendarDate(cv?.ngayKetThuc) ?? parentEnd ?? now,
    },
  };
}

/** Paste/clone: gắn cha–con cho cả nhánh, không chỉ node gốc. */
export function ganIdentityConChoCaCay(child: TableRecord, parent: TableRecord): TableRecord {
  const next = applyApv2ChildTaskFields(child, parent);
  const childs = ((next.childs || []) as TableRecord[]).map((c) =>
    ganIdentityConChoCaCay(c, next)
  );
  return childs.length ? { ...next, childs } : next;
}

function isApv2HiddenDuAnRootWithChildren(record: TableRecord): boolean {
  const cv = record.congViec;
  if (!cv) return false;
  const childs = record.childs ?? [];
  if (childs.length === 0) return false;
  if (isApv2VisibleMucTieuRootTask(cv)) return false;
  if (cv.isDuAn === true) return true;
  const id = String(cv.id ?? '').trim();
  const duAnId = String(cv.duAnID ?? '').trim();
  if (
    (cv.level ?? 0) === 0 &&
    id &&
    duAnId &&
    id === duAnId &&
    !isApv2RootTaskParentId(cv.parentID)
  ) {
    return true;
  }
  const firstChild = childs[0] as TableRecord;
  if (firstChild && isApv2LegacyHiddenDuAnWrapper(cv, String(firstChild.congViec?.parentID ?? ''))) {
    return true;
  }
  return false;
}

// #region foreignDisplayRoot

/** Tổ tiên thật của root hiển thị (BE `duongDanCha`, CORE-21) — gốc → cha trực tiếp. */
export type Apv2AncestorRef = { id: string; tenCongViec?: string | null };

/** Dòng phân nhóm «Được giao từ cây khác» — key theo prefix group header để bảng/Timeline coi là dòng nhóm. */
export const APV2_FOREIGN_ROOT_GROUP_KEY_PREFIX = 'apv2-group:apv2-foreign-roots:';
export const APV2_FOREIGN_ROOT_GROUP_LABEL = 'Được giao từ cây khác';
export const APV2_FOREIGN_ROOT_HINT =
  'Việc được giao nằm trong cây của người khác — không sắp xếp cùng việc gốc của bạn';

export function isApv2ForeignRootGroupRecord(record?: TableRecord | null): boolean {
  return String(record?.key ?? '').startsWith(APV2_FOREIGN_ROOT_GROUP_KEY_PREFIX);
}

/**
 * Root hiển thị (CORE-21): dòng nằm ngay dưới node MT ảo nhưng có cha thật (`parentID` khác rỗng / khác apv2-mt-*)
 * không có trong tập đang hiển thị. Đánh dấu khi flatten (`isForeignDisplayRoot`) hoặc suy từ `duongDanCha`.
 */
export function isApv2ForeignDisplayRoot(record?: TableRecord | null): boolean {
  if (!record || record.isPlaceholder) return false;
  const r = record as TableRecord & { isForeignDisplayRoot?: boolean; duongDanCha?: unknown };
  if (r.isForeignDisplayRoot === true) return true;
  return Array.isArray(r.duongDanCha) && r.duongDanCha.length > 0;
}

export function readApv2DuongDanCha(record?: TableRecord | null): Apv2AncestorRef[] {
  const raw = (record as { duongDanCha?: unknown; DuongDanCha?: unknown } | null)?.duongDanCha
    ?? (record as { DuongDanCha?: unknown } | null)?.DuongDanCha;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((a) => {
      const item = (a ?? {}) as Record<string, unknown>;
      return {
        id: String(item.id ?? item.Id ?? ''),
        tenCongViec: String(item.tenCongViec ?? item.TenCongViec ?? '').trim() || null,
      };
    })
    .filter((a) => a.id || a.tenCongViec);
}

function isForeignRootUnderMucTieu(item: TableRecord): boolean {
  if (item.isPlaceholder || isDisplayMucTieuRecord(item)) return false;
  if (isApv2HiddenDuAnRootWithChildren(item)) return false;
  return !isApv2RootTaskParentId(String(item.congViec?.parentID ?? '').trim());
}

function buildForeignRootGroupRecord(mtctid: string, count: number): TableRecord {
  const key = `${APV2_FOREIGN_ROOT_GROUP_KEY_PREFIX}${mtctid}`;
  return {
    key,
    isGroupHeader: true,
    isForeignRootGroup: true,
    groupLabel: APV2_FOREIGN_ROOT_GROUP_LABEL,
    groupCount: count,
    hasChild: true,
    congViec: { id: key, tenCongViec: APV2_FOREIGN_ROOT_GROUP_LABEL, mtctid },
    childs: [],
  } as unknown as TableRecord;
}

// #endregion foreignDisplayRoot

function flattenApv2MucTieuForestInPlace(
  items: TableRecord[],
  options: {
    expandedKeysSet?: Set<string>;
    parentNumber?: string;
    /** Đếm STT công việc cùng cấp (reset khi xuống cấp / vào dưới mục tiêu). */
    siblingCounter?: { n: number };
    /** Đếm STT mục tiêu toàn forest (1, 2, 3…) — không reset khi unwrap node ẩn. */
    mucTieuCounter?: { n: number };
    /** Giữ thứ tự sibling hiện có (vd. sort ngày hiển thị) — bỏ reorder viTri. */
    preserveSiblingOrder?: boolean;
    /** Cấp ngay dưới node MT ảo: gốc thật trước, rồi dòng nhóm «Được giao từ cây khác» + root hiển thị (CORE-21). */
    foreignRootGroupMtctid?: string;
  }
): TableRecord[] {
  const {
    expandedKeysSet,
    parentNumber = '',
    siblingCounter = { n: 0 },
    mucTieuCounter = { n: 0 },
    preserveSiblingOrder = false,
    foreignRootGroupMtctid,
  } = options;
  const expandAll = expandedKeysSet == null;
  const result: TableRecord[] = [];

  const assignTaskNum = (): string => {
    siblingCounter.n += 1;
    return parentNumber ? `${parentNumber}.${siblingCounter.n}` : `${siblingCounter.n}`;
  };

  const isExpanded = (item: TableRecord): boolean =>
    expandAll || expandedKeysSet!.has(String(item.key));

  const sortedItems = preserveSiblingOrder ? items : orderApv2SiblingRecordsByViTri(items);
  let orderedItems = sortedItems;
  let foreignGroupBeforeId = '';
  let foreignCount = 0;
  if (foreignRootGroupMtctid != null) {
    const own: TableRecord[] = [];
    const foreign: TableRecord[] = [];
    for (const item of sortedItems) {
      if (isForeignRootUnderMucTieu(item)) {
        (item as TableRecord & { isForeignDisplayRoot?: boolean }).isForeignDisplayRoot = true;
        foreign.push(item);
      } else {
        delete (item as TableRecord & { isForeignDisplayRoot?: boolean }).isForeignDisplayRoot;
        own.push(item);
      }
    }
    if (foreign.length) {
      orderedItems = [...own, ...foreign];
      foreignGroupBeforeId = String(foreign[0].key);
      foreignCount = foreign.length;
    }
  }

  for (const item of orderedItems) {
    if (foreignGroupBeforeId && String(item.key) === foreignGroupBeforeId) {
      result.push(buildForeignRootGroupRecord(foreignRootGroupMtctid ?? '', foreignCount));
    }
    if (item.isPlaceholder) {
      result.push(item);
      continue;
    }

    if (isDisplayMucTieuRecord(item)) {
      // Số thứ tự mục tiêu toàn cục (1, 2, 3…); công việc bên dưới đếm lại từ 1.
      mucTieuCounter.n += 1;
      item.orderNumber = `${mucTieuCounter.n}`;
      result.push(item);
      if (isExpanded(item) && item.childs?.length) {
        result.push(
          ...flattenApv2MucTieuForestInPlace(item.childs as TableRecord[], {
            expandedKeysSet,
            parentNumber: '',
            siblingCounter: { n: 0 },
            mucTieuCounter,
            preserveSiblingOrder,
            foreignRootGroupMtctid: String(item.congViec?.mtctid ?? item.key ?? ''),
          })
        );
      }
      continue;
    }

    if (isApv2HiddenDuAnRootWithChildren(item)) {
      // Giữ cùng siblingCounter + mucTieuCounter — không reset STT khi unwrap.
      result.push(
        ...flattenApv2MucTieuForestInPlace(item.childs as TableRecord[], {
          expandedKeysSet,
          parentNumber,
          siblingCounter,
          mucTieuCounter,
          preserveSiblingOrder,
        })
      );
      continue;
    }

    const num = assignTaskNum();
    item.orderNumber = num;
    result.push(item);

    if (isExpanded(item) && item.childs?.length) {
      result.push(
        ...flattenApv2MucTieuForestInPlace(item.childs as TableRecord[], {
          expandedKeysSet,
          parentNumber: num,
          siblingCounter: { n: 0 },
          mucTieuCounter,
          preserveSiblingOrder,
        })
      );
    }
  }

  return result;
}

export function flattenApv2MucTieuForestExpandedInPlace(
  items: TableRecord[],
  expandedKeysSet: Set<string>,
  parentNumber = '',
  options?: { preserveSiblingOrder?: boolean }
): TableRecord[] {
  return flattenApv2MucTieuForestInPlace(items, {
    expandedKeysSet,
    parentNumber,
    siblingCounter: { n: 0 },
    mucTieuCounter: { n: 0 },
    preserveSiblingOrder: options?.preserveSiblingOrder,
  });
}

export function flattenApv2MucTieuForestAllInPlace(
  items: TableRecord[],
  parentNumber = '',
  options?: { preserveSiblingOrder?: boolean }
): TableRecord[] {
  return flattenApv2MucTieuForestInPlace(items, {
    parentNumber,
    siblingCounter: { n: 0 },
    mucTieuCounter: { n: 0 },
    preserveSiblingOrder: options?.preserveSiblingOrder,
  });
}

function removeTaskByIdFromTree(items: TableRecord[], taskId: string): TableRecord[] {
  return items
    .filter((item) => String(item.congViec?.id ?? item.key) !== taskId)
    .map((item) => {
      const childs = (item.childs as TableRecord[]) ?? [];
      if (!childs.length) return item;
      const nextChilds = removeTaskByIdFromTree(childs, taskId);
      if (nextChilds.length === childs.length) return item;
      return {
        ...item,
        childs: nextChilds,
        hasChild: nextChilds.length > 0,
        countChilds: nextChilds.length,
      };
    });
}

export function getMucTieuDisplayChildTasks(items: TableRecord[], mtctid: string): TableRecord[] {
  for (const item of items || []) {
    if (isDisplayMucTieuRecord(item) && String(item.congViec?.mtctid ?? '') === mtctid) {
      return (item.childs || []) as TableRecord[];
    }
    if (item.childs?.length) {
      const found = getMucTieuDisplayChildTasks(item.childs as TableRecord[], mtctid);
      if (found.length > 0) return found;
    }
  }
  return [];
}

/** Patch tên node display mục tiêu khi map tên (mucTieuNameById) có sẵn sau khi forest đã load. */
export function patchMucTieuDisplayNodeNames(
  items: DuAnCongViecTree[],
  mucTieuNameById: Record<string, string> = {}
): DuAnCongViecTree[] {
  const resolveName = (mtctid: string): string => {
    const clean = mtctid.includes('_') ? mtctid.split('_').pop() || mtctid : mtctid;
    return (
      String(mucTieuNameById[mtctid] ?? '').trim() ||
      String(mucTieuNameById[clean] ?? '').trim() ||
      ''
    );
  };
  let changed = false;
  const next = (items || []).map((item) => {
    if (isDisplayMucTieuRecord(item)) {
      const mtctid = String(item.congViec?.mtctid ?? '').trim();
      const name = resolveName(mtctid);
      const currentName = String(item.congViec?.tenCongViec ?? '').trim();
      if (name && name !== currentName) {
        changed = true;
        return {
          ...item,
          congViec: {
            ...item.congViec,
            tenCongViec: name,
            treePathName: name,
          },
        };
      }
    }
    if (item.childs?.length) {
      const nextChilds = patchMucTieuDisplayNodeNames(item.childs, mucTieuNameById);
      if (nextChilds !== item.childs) {
        changed = true;
        return { ...item, childs: nextChilds };
      }
    }
    return item;
  });
  return changed ? next : items;
}

export function patchMucTieuDisplayNodeChilds(
  items: TableRecord[],
  mtctid: string,
  patchChilds: (childs: TableRecord[]) => TableRecord[]
): TableRecord[] {
  return (items || []).map((item) => {
    if (isDisplayMucTieuRecord(item) && String(item.congViec?.mtctid ?? '') === mtctid) {
      const nextChilds = patchChilds((item.childs || []) as TableRecord[]);
      return {
        ...item,
        childs: nextChilds,
        hasChild: nextChilds.length > 0,
        countChilds: nextChilds.length,
      };
    }
    if (item.childs?.length) {
      return {
        ...item,
        childs: patchMucTieuDisplayNodeChilds(item.childs as TableRecord[], mtctid, patchChilds),
      };
    }
    return item;
  });
}

export function insertApv2RootTaskUnderMucTieu(
  items: TableRecord[],
  mtctid: string,
  newTask: TableRecord
): { tree: TableRecord[]; displayKey?: string | number } {
  const taskId = String(newTask.congViec?.id ?? newTask.key ?? '');
  const baseItems = taskId ? removeTaskByIdFromTree(items, taskId) : items;

  const insertInto = (item: TableRecord): TableRecord => {
    const nextChilds = [...((item.childs || []) as TableRecord[]), newTask];
    return {
      ...item,
      childs: nextChilds,
      hasChild: true,
      countChilds: nextChilds.length,
    };
  };

  let displayKey: string | number | undefined;
  let inserted = false;
  const targetMtctid = mtctid || resolveMtctidForNewRootTask(baseItems);

  const walk = (list: TableRecord[]): TableRecord[] =>
    list.map((item) => {
      if (inserted || item.isPlaceholder) return item;
      if (isDisplayMucTieuRecord(item)) {
        const itemMtctid = String(item.congViec?.mtctid ?? '');
        if (targetMtctid && itemMtctid !== targetMtctid) {
          if (item.childs?.length) return { ...item, childs: walk(item.childs as TableRecord[]) };
          return item;
        }
        inserted = true;
        displayKey = item.key ?? item.congViec?.id;
        return insertInto(item);
      }
      if (item.childs?.length) return { ...item, childs: walk(item.childs as TableRecord[]) };
      return item;
    });

  const tree = walk(baseItems);
  // KHÔNG fallback chèn vào display node khác khi mtctid không khớp — tránh task
  // mang `mtctid` lệch → sau lưu/reload BE trả về đúng mtctid → task nhảy sheet / bị double.
  // Caller (`insertRootTaskIntoMucTieuForest`) tự tạo display node cho mtctid nếu chưa có.
  return { tree, displayKey };
}

/** Chuẩn hóa loại trừ tiTrong / tiTrongCal (+ cặp V2) trên cây. */
function normalizeMutualExclusiveTiTrong(record: TableRecord): TableRecord {
  const cv = record.congViec;
  if (!cv) return record;

  const ex = apv2MutualExclusiveFields(cv.tiTrong, cv.tiTrongCal);
  const tiFields = apv2ToTreeCongViecTiTrong(ex.tiTrong, ex.tiTrongCal);
  const ex2 = apv2MutualExclusiveFields(cv.tiTrongV2, cv.tiTrongCalV2);
  const tiFields2 = apv2ToTreeCongViecTiTrong(ex2.tiTrong, ex2.tiTrongCal);
  const childs = record.childs?.length
    ? (record.childs as TableRecord[]).map(normalizeMutualExclusiveTiTrong)
    : record.childs;

  const sameTi = (cv.tiTrong ?? undefined) === tiFields.tiTrong;
  const sameCal = (cv.tiTrongCal ?? undefined) === tiFields.tiTrongCal;
  const sameTi2 = (cv.tiTrongV2 ?? undefined) === tiFields2.tiTrong;
  const sameCal2 = (cv.tiTrongCalV2 ?? undefined) === tiFields2.tiTrongCal;
  if (sameTi && sameCal && sameTi2 && sameCal2 && childs === record.childs) return record;

  return {
    ...record,
    congViec: {
      ...cv,
      tiTrong: tiFields.tiTrong,
      tiTrongCal: tiFields.tiTrongCal,
      tiTrongV2: tiFields2.tiTrong,
      tiTrongCalV2: tiFields2.tiTrongCal,
    },
    childs,
  } as TableRecord;
}

function normalizeMutualExclusiveTiTrongTree(items: TableRecord[]): TableRecord[] {
  return items.map(normalizeMutualExclusiveTiTrong);
}

export { normalizeMutualExclusiveTiTrongTree };

export function resolveSelectedMtctids(csv: string, allItems: MucTieuListItem[]): string[] {
  const raw = String(csv || '').trim();
  if (raw === '-1') return [];
  if (!raw || raw === '0') return allItems.map((item) => item.id).filter(Boolean);
  return [...new Set(raw.split(',').map((id) => id.trim()).filter(Boolean))];
}

function normalizeCongViecDates(cv: Record<string, unknown>): Record<string, unknown> {
  if (!cv || typeof cv !== 'object') return cv;
  const next = { ...cv };
  if (next.ngayBatDau != null && next.ngayBatDau !== '') {
    next.ngayBatDau = toActionPlanCalendarDate(next.ngayBatDau as Date | string);
  }
  if (next.ngayKetThuc != null && next.ngayKetThuc !== '') {
    next.ngayKetThuc = toActionPlanCalendarDate(next.ngayKetThuc as Date | string);
  }
  return next;
}

function normalizeNhomCongViec(raw: unknown): DuAnCongViecTree['nhomCongViec'] {
  const v = String(raw ?? '')
    .trim()
    .toLowerCase();
  if (v === 'pt' || v === 'dcg' || v === 'dg' || v === 'ht' || v === 'khac') return v;
  return null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeDuAnCongViecTree(node: any): DuAnCongViecTree {
  const rawChilds = node?.childs ?? node?.Childs ?? [];
  const childs = Array.isArray(rawChilds)
    ? rawChilds.map((child: unknown) => normalizeDuAnCongViecTree(child))
    : [];
  const rawCongViec = node?.congViec ?? node?.CongViec ?? {};
  const congViec = normalizeCongViecDates(rawCongViec);
  const rawValues = node?.columnValues ?? node?.ColumnValues;
  return {
    ...node,
    congViec,
    childs,
    hasChild: childs.length > 0 || node?.hasChild === true || node?.HasChild === true,
    countChilds: childs.length || node?.countChilds || node?.CountChilds || 0,
    columnValues: Array.isArray(rawValues) ? rawValues : node?.columnValues ?? [],
    nhomCongViec: normalizeNhomCongViec(node?.nhomCongViec ?? node?.NhomCongViec),
  };
}

export function normalizeDuAnCongViecTrees(trees: DuAnCongViecTree[]): DuAnCongViecTree[] {
  return (trees || []).map((node) => normalizeDuAnCongViecTree(node));
}

/** Danh sách mục tiêu bên trái + dữ liệu cho TargetSelect — từ actionplanv2/loadmuctieu. */
export async function loadMucTieuList(params: LoadMucTieuListParams): Promise<MucTieuListLoadResult> {
  const { idSelect, year, dateChoice, filter, includeEmployeeHomeSummary } = params;
  if (!idSelect) return { items: [], filterData: null, summary: null };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const res: any = await actionPlanV2Api.loadMucTieu({
    idChoice: idSelect,
    year,
    dateChoice,
    phanHe: ACTION_PLAN_V2.PHAN_HE,
    filter,
    includeEmployeeHomeSummary,
  });

  if (!res?.success || !res?.data) {
    return { items: [], filterData: null, summary: null };
  }

  const mapped = mapLoadMucTieuV2Response(res.data as Record<string, unknown>);
  const summaryRaw = (res.data as Record<string, unknown>).summary
    ?? (res.data as Record<string, unknown>).Summary;
  const summary = includeEmployeeHomeSummary
    ? normalizeEmployeeHomeSummaryResponse(summaryRaw)
    : undefined;

  return {
    ...mapped,
    summary,
  };
}
